import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import analytics from '../services/analytics';
import { formatChannelEventDate } from '../utils/channelDateUtils';
import { FALLBACK_IMAGE_SRC } from '../utils/ImageUtils';
import { imageUrl as v1ImageUrl, imageSrcSet } from '../api/image';
import GeoUnavailable from './common/GeoUnavailable';
import ProgressBar from './common/ProgressBar';
import LiveBadge from './LiveBadge';
import WatchOn from './common/WatchOn';
import { useMediaState } from '../hooks/useMediaState';
import './ChannelCard.css';
import { getPosterSize } from '../config/categoryRailSettings';

/**
 * One v1 card (`cardToChannel`): a media card opens its watch page, an event or fighter card its
 * page, an article its bkfc.com page. `clickable: false` (a See all card whose `link` is null) is
 * inert.
 */
const ChannelCard = React.memo(({ channel = {}, style, index, externalClickHandle }) => {
  const safeChannel = channel || {};
  const navigate = useNavigate();

  // A media card's server state, kept fresh by the shared /v1/live poll. A card airing on a
  // partner (`watch_on`, media or event) shows the partner instead of a live badge.
  const isMedia = safeChannel.entityType === 'media';
  const media = useMediaState(isMedia ? safeChannel : null);
  const watchOn = isMedia ? media.watch_on : safeChannel.watch_on;
  const isLive = media.state === 'LIVE' && !watchOn;

  const isTopNumber = style === 'top-number' || style === 'top-number-poster';
  const isPosterFormat = style === 'top-number-poster' || style === 'poster';
  const isContinueWatching = style === 'continue-watching';

  const v1Crop = isPosterFormat ? '2x3' : '16x9';
  const v1Asset = safeChannel.art
    ? (isPosterFormat && safeChannel.art.portrait) ||
      safeChannel.art.landscape ||
      safeChannel.art.portrait
    : null;
  const geoBlocked = safeChannel.geo_ok === false;
  const imageUrl = v1ImageUrl(v1Asset, v1Crop, isPosterFormat ? 320 : 480) || FALLBACK_IMAGE_SRC;
  const title = safeChannel.title || 'Untitled';
  const eventDateLabel = formatChannelEventDate(safeChannel.starts_at);

  const [imageError, setImageError] = useState(false);

  const clickable = safeChannel.clickable !== false;
  const handleClick = () => {
    analytics.trackSelectContent({
      id: safeChannel._id,
      title: safeChannel.title,
      type: safeChannel.entityType,
    });

    if (externalClickHandle) {
      return externalClickHandle();
    }
    if (safeChannel.link) {
      return navigate(safeChannel.link);
    }
    if (safeChannel.href) {
      return window.open(safeChannel.href, '_blank', 'noopener');
    }
    return navigate(`/watch/${safeChannel.slug || safeChannel._id}`);
  };

  const posterSize = isPosterFormat || isTopNumber ? getPosterSize(window.innerWidth) : null;
  const resume = safeChannel.resumption_data;

  return (
    <div
      className={`w-full min-w-0 ${clickable ? 'cursor-pointer ' : ''}channel-card ${isTopNumber ? 'top-number-card' : ''}`}
      onClick={clickable ? handleClick : undefined}
    >
      <div className="relative overflow-hidden rounded-lg">
        {isLive && (
          <div className="absolute z-20 top-2 left-2">
            <LiveBadge />
          </div>
        )}

        {watchOn && (
          <div className="absolute z-20 top-2 left-2">
            <WatchOn watchOn={watchOn} badge />
          </div>
        )}

        {isTopNumber && (
          <div className="absolute top-0 left-0 z-10 number-overlay">
            <span className="number-text">{index + 1}</span>
          </div>
        )}

        {geoBlocked && <GeoUnavailable />}

        <img
          src={imageError ? FALLBACK_IMAGE_SRC : imageUrl}
          srcSet={!imageError && v1Asset ? imageSrcSet(v1Asset, v1Crop, 960) : undefined}
          sizes={v1Asset ? (isPosterFormat ? '320px' : '480px') : undefined}
          alt={title}
          style={isTopNumber ? { width: posterSize?.width, height: posterSize?.height } : undefined}
          className={`object-cover w-full transition-transform duration-300 hover:scale-105 ${
            isPosterFormat ? 'aspect-[2/3]' : 'aspect-video'
          }${geoBlocked ? ' opacity-40 grayscale' : ''}`}
          onError={() => setImageError(true)}
          loading="lazy"
        />

        {isContinueWatching && resume && (
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
            <ProgressBar
              progressData={{
                title,
                progress: resume.progress,
                point: resume.point,
                duration: resume.duration,
                isSeries: false,
                showResumeText: true,
              }}
              variant="card"
              size="small"
              showProgressBar={true}
              showEpisodeInfo={false}
              className="text-white"
            />
          </div>
        )}
      </div>
      <div className="channel-card-meta">
        <h3 className="channel-card-title">{title}</h3>
        {eventDateLabel && <p className="channel-card-date">{eventDateLabel}</p>}
      </div>
    </div>
  );
});

export default ChannelCard;
